import { GOALS, MATERIALS, normaliseProfile, makePlan, snapshot, restore, exportText } from './kg-rituali-core.js';

const STORAGE_KEY = 'karmagick:ritual:v1';

/** Build text-only DOM. Neither personal text nor model replies are interpreted as HTML. */
function node(tag, attrs = {}, ...children) {
  const element = document.createElement(tag);
  for (const [key, value] of Object.entries(attrs)) {
    if (key.startsWith('on')) element.addEventListener(key.slice(2), value);
    else if (key === 'class') element.className = value;
    else if (key === 'checked' || key === 'disabled') element[key] = Boolean(value);
    else if (value !== null && value !== undefined) element.setAttribute(key, String(value));
  }
  for (const child of children.flat(Infinity)) {
    if (child !== null && child !== undefined) element.append(child instanceof Node ? child : document.createTextNode(String(child)));
  }
  return element;
}

const decode=value=>{if(typeof value==='string'){const box=document.createElement('textarea');box.innerHTML=value;return box.value;}if(Array.isArray(value))return value.map(decode);if(value&&typeof value==='object')return Object.fromEntries(Object.entries(value).map(([key,item])=>[key,decode(item)]));return value;};

/** Portable isolated island: same component in the Shopify section and the local API preview. */
class RitualGuide extends HTMLElement {
  connectedCallback() {
    if (this.life) return;
    this.life = new AbortController();
    try { this.content = decode(JSON.parse(this.querySelector('[data-ritual-content]').textContent)); }
    catch { this.life = null; return; } // Keep the server-rendered fallback readable.
    this.ui = this.content.ui;
    this.locale = this.getAttribute('lang') === 'it' ? 'it' : 'en';
    this.endpoint = this.safeEndpoint(this.dataset.endpoint);
    this.panel = this.querySelector('[data-ritual-panel]');
    this.live = this.querySelector('[data-ritual-status]');
    this.querySelector('[data-ritual-fallback]').hidden = true;
    this.profile = normaliseProfile();
    this.stage = 'intention';
    this.index = 0;
    this.skipped = [];
    this.variants = {};
    this.history = [];
    this.draftMessage = '';
    this.busy = false;
    this.pending = null;
    this.readSaved();
    this.render(false);
  }

  disconnectedCallback() {
    this.life?.abort();
    this.pending?.abort();
    this.life = null;
  }

  safeEndpoint(raw) {
    if (!raw) return '';
    try {
      const url = new URL(raw, location.origin);
      const sameOrigin = url.origin === location.origin;
      if (sameOrigin || (url.protocol === 'https:' && !url.username && !url.password)) return url.href;
    } catch {}
    return '';
  }

  say(message = '') { this.live.textContent = message; }
  button(label, action, secondary = false, attrs = {}) {
    return node('button', { type: 'button', class: secondary ? 'kg-ritual__secondary' : 'kg-ritual__primary', onclick: action, ...attrs }, label);
  }
  heading(title, hint = '') {
    return node('header', { class: 'kg-ritual__question' }, node('h2', { tabindex: '-1' }, title), hint ? node('p', {}, hint) : null);
  }
  navigate(stage) {
    // Abandoning a screen invalidates its request: a late response must not replace new choices.
    this.pending?.abort();
    this.pending = null;
    this.busy = false;
    this.stage = stage;
    this.draftMessage = '';
    this.say();
    this.render();
  }
  field(label, value, update, max = 240, multiline = false) {
    const id = `kr-${this.dataset.instance || 'local'}-${++this.fieldIndex}`;
    const input = node(multiline ? 'textarea' : 'input', {
      id, maxlength: max, rows: multiline ? '3' : null,
      type: multiline ? null : 'text',
      oninput: event => update(event.target.value),
    });
    input.value = value;
    return node('div', { class: 'kg-ritual__field' }, node('label', { for: id }, label), input);
  }
  choice(label, hint, name, value, checked, update, type = 'radio') {
    return node('label', { class: 'kg-ritual__choice' }, node('input', {
      type, name, value, checked, onchange: event => update(event.target.checked),
    }), node('span', {}, node('strong', {}, label), hint ? node('small', {}, hint) : null));
  }
  form(title, hint, body, submit, back) {
    const form = node('form', { onsubmit: event => { event.preventDefault(); submit(); } }, this.heading(title, hint), body);
    form.append(node('div', { class: 'kg-ritual__actions' }, node('button', { type: 'submit', class: 'kg-ritual__primary' }, this.ui.next), this.button(this.ui.previous, back, true)));
    return form;
  }

  chat() {
    if (!this.endpoint) return null;
    const textarea = node('textarea', {
      id: `kr-chat-${this.dataset.instance || 'local'}`, rows: '3', maxlength: '600', placeholder: this.ui.tell_placeholder,
      oninput: event => { this.draftMessage = event.target.value; }, disabled: this.busy,
    });
    textarea.value = this.draftMessage;
    const form = node('form', { class: 'kg-ritual__chat', onsubmit: event => { event.preventDefault(); this.ask(); } },
      node('label', { for: textarea.id }, this.stage === 'intention' ? this.ui.tell_label : this.ui.ai_label), textarea,
      node('p', { class: 'kg-ritual__hint' }, this.ui.consent),
      node('button', { type: 'submit', class: 'kg-ritual__primary', disabled: this.busy }, this.busy ? this.ui.thinking : this.ui.send));
    const output = node('div', { class: 'kg-ritual__dialogue' });
    for (const item of this.history.slice(-4)) output.append(node('div', { class: `kg-ritual__message kg-ritual__message--${item.role}` },
      node('span', { class: 'kg-ritual__eyebrow' }, item.role === 'assistant' ? this.ui.ai_label : this.ui.chosen_words), node('p', {}, item.content)));
    if (this.suggestion) {
      const goal = this.content.goals[this.suggestion.goal];
      const refining = this.stage === 'words';
      output.append(this.button(`${refining ? this.ui.use_words : this.ui.use_suggestion} · ${goal.label}`, () => {
        const suggestion = this.suggestion;
        this.selectGoal(suggestion.goal, suggestion.focus);
        if (suggestion.phrase && suggestion.action) {
          this.profile.phrase = suggestion.phrase;
          this.profile.action = suggestion.action;
        }
        this.refineOpen = false;
        this.navigate(refining ? 'words' : 'focus');
      }, true, { 'data-kr-suggestion': '' }));
      if (this.suggestion.phrase && this.suggestion.action) {
        output.append(node('div', { class: 'kg-ritual__after' }, node('h3', {}, this.ui.proposed_words),
          node('p', {}, this.suggestion.phrase), node('p', { class: 'kg-ritual__hint' }, `${this.ui.afterwards}: ${this.suggestion.action}`)));
      }
    }
    return node('div', { class: 'kg-ritual__conversation' }, output, form);
  }

  async ask() {
    if (this.busy || !this.endpoint) return;
    const message = this.draftMessage.trim();
    if (!message) { this.say(this.ui.text_required); return; }
    const controller = new AbortController();
    this.pending = controller;
    this.busy = true;
    if (this.stage === 'words') this.refineOpen = true;
    this.suggestion = null;
    this.render(false);
    this.say(this.ui.thinking);
    const timeout = setTimeout(() => controller.abort(), 30000);
    try {
      const response = await fetch(this.endpoint, {
        method: 'POST', headers: { 'content-type': 'application/json' }, signal: controller.signal, credentials: 'omit',
        body: JSON.stringify({ locale: this.locale, mode: this.stage === 'intention' ? 'intention' : 'refine', message, history: this.history.slice(-6), profile: this.profile }),
      });
      if (!response.ok) throw new Error(response.status === 429 ? 'limit' : 'service');
      const data = await response.json();
      const reply = data.reply;
      if (!reply || !['suggestion', 'clarify', 'boundary'].includes(reply.status) || typeof reply.message !== 'string' || reply.message.length > 600) throw new Error('contract');
      if (reply.status === 'suggestion' && !GOALS[reply.goal]?.includes(reply.focus)) throw new Error('contract');
      if (controller !== this.pending || !this.isConnected) return;
      this.history = [...this.history, { role: 'user', content: message }, { role: 'assistant', content: reply.message }].slice(-6);
      this.suggestion = reply.status === 'suggestion' ? { goal: reply.goal, focus: reply.focus,
        phrase: typeof reply.phrase === 'string' ? reply.phrase.slice(0,240) : '', action: typeof reply.action === 'string' ? reply.action.slice(0,240) : '' } : null;
      this.draftMessage = '';
      this.say(reply.message);
    } catch (error) {
      if (controller !== this.pending || !this.isConnected) return;
      this.say(error.message === 'limit' ? this.ui.ai_limit : this.ui.ai_error);
    } finally {
      clearTimeout(timeout);
      if (controller === this.pending) {
        this.busy = false;
        this.pending = null;
        this.render(false);
        this.panel.querySelector('.kg-ritual__dialogue')?.scrollIntoView({ block: 'nearest' });
        this.panel.querySelector('.kg-ritual__chat textarea')?.focus({ preventScroll: true });
      }
    }
  }

  selectGoal(goal, focus = GOALS[goal][0]) {
    if (this.profile.goal !== goal || this.profile.focus !== focus) {
      this.profile.phrase = '';
      this.profile.action = '';
      this.variants = {};
      this.skipped = [];
    }
    this.profile.goal = goal;
    this.profile.focus = focus;
    this.suggestion = null;
  }

  intentionScreen() {
    const choices = node('div', { class: 'kg-ritual__goals' });
    for (const [id, goal] of Object.entries(this.content.goals)) choices.append(node('button', {
      type: 'button', class: 'kg-ritual__goal', 'data-kr-goal': id,
      onclick: () => { this.selectGoal(id, this.profile.goal === id ? this.profile.focus : GOALS[id][0]); this.navigate('focus'); },
    }, node('span', {}, node('strong', {}, goal.label), node('small', {}, goal.hint)), node('span', { 'aria-hidden': 'true' }, '↗')));
    const body = node('div', {}, this.heading(this.ui.goal_question, this.ui.goal_hint), this.chat(), choices);
    if (this.saved) body.append(node('aside', { class: 'kg-ritual__saved' }, node('p', {}, this.ui.restore_hint),
      this.button(this.ui.restore, () => this.loadSaved(), true), this.button(this.ui.forget, () => this.forget(), true)));
    return body;
  }

  focusScreen() {
    const goal = this.content.goals[this.profile.goal];
    const options = node('fieldset', { class: 'kg-ritual__options' }, node('legend', { class: 'kg-ritual__sr' }, goal.question));
    for (const [id, focus] of Object.entries(goal.focuses)) options.append(this.choice(focus.label, '', 'focus', id, this.profile.focus === id, () => this.selectGoal(this.profile.goal, id)));
    return this.form(goal.question, goal.why, options, () => this.navigate('context'), () => this.navigate('intention'));
  }

  contextScreen() {
    const times = node('fieldset', {}, node('legend', {}, this.ui.time_label), node('div', { class: 'kg-ritual__row' }, [5, 10, 15].map(minutes => this.choice(`${minutes} ${this.ui.minute}`, '', 'minutes', minutes, this.profile.minutes === minutes, () => { this.profile.minutes = minutes; }))));
    const settings = node('fieldset', {}, node('legend', {}, this.ui.setting_label), node('div', { class: 'kg-ritual__options' },
      [false, true].map(discreet => this.choice(discreet ? this.ui.discreet : this.ui.private, '', 'setting', String(discreet), this.profile.discreet === discreet, () => { this.profile.discreet = discreet; this.variants = {}; }))));
    const materials = node('fieldset', {}, node('legend', {}, this.ui.materials_label), node('p', { class: 'kg-ritual__hint' }, this.ui.materials_hint));
    const options = node('div', { class: 'kg-ritual__materials' });
    const none = this.choice(this.ui.none, '', 'materials', 'none', !this.profile.materials.length, () => { this.profile.materials = []; this.render(false); }, 'checkbox');
    options.append(none);
    for (const material of MATERIALS) options.append(this.choice(this.ui[material], '', 'materials', material, this.profile.materials.includes(material), checked => {
      this.profile.materials = checked ? [...new Set([...this.profile.materials, material])] : this.profile.materials.filter(item => item !== material);
      none.querySelector('input').checked = !this.profile.materials.length;
      this.variants = {};
    }, 'checkbox'));
    materials.append(options);
    return this.form(this.ui.context_title, '', [times, settings, materials], () => this.navigate('words'), () => this.navigate('focus'));
  }

  wordsScreen() {
    const focus = this.content.goals[this.profile.goal].focuses[this.profile.focus];
    if (!this.profile.phrase) this.profile.phrase = focus.phrase;
    if (!this.profile.action) this.profile.action = focus.action;
    const form = this.form(this.ui.words_title, focus.prompt, [
      this.field(this.ui.phrase_label, this.profile.phrase, value => { this.profile.phrase = value; }, 240, true),
      this.field(this.ui.action_label, this.profile.action, value => { this.profile.action = value; }, 240, true),
      node('p', { class: 'kg-ritual__hint' }, this.ui.action_hint),
    ], () => this.navigate('preview'), () => this.navigate('context'));
    form.querySelector('[type=submit]').textContent = this.ui.preview;
    return node('div', {}, form, this.endpoint ? node('details', { class: 'kg-ritual__refine', open: this.refineOpen ? '' : null,
      ontoggle: event => { if (event.target.isConnected) this.refineOpen = event.target.open; } }, node('summary', {}, this.ui.send), this.chat()) : null);
  }

  planMeta(plan) {
    return node('p', { class: 'kg-ritual__meta' }, `${plan.profile.minutes} ${this.ui.minute} · ${plan.material === 'none' ? this.ui.no_object : this.ui[plan.material]}`);
  }
  keepActions() {
    return node('div', { class: 'kg-ritual__keep' },
      this.button(this.ui.save, () => this.save(), true), this.button(this.ui.download, () => this.download(), true),
      this.saved ? this.button(this.ui.forget, () => this.forget(), true) : null);
  }
  previewScreen() {
    const plan = makePlan(this.profile, this.content);
    const sequence = node('ol', { class: 'kg-ritual__sequence' });
    for (const step of plan.steps) sequence.append(node('li', {}, node('h3', {}, step.title), node('p', {}, step.body)));
    return node('div', {}, this.heading(this.ui.summary, `${plan.goal} · ${plan.title}`), this.planMeta(plan),
      node('blockquote', { class: 'kg-ritual__phrase' }, plan.phrase),
      node('details', { class: 'kg-ritual__meaning' }, node('summary', {}, this.ui.why), node('p', {}, plan.why)),
      node('div', { class: 'kg-ritual__after' }, node('h3', {}, this.ui.afterwards), node('p', {}, plan.action)),
      node('div', { class: 'kg-ritual__actions' }, this.button(this.ui.begin, () => { this.index = 0; this.skipped = []; this.variants = {}; this.navigate('practise'); }), this.button(this.ui.edit, () => this.navigate('intention'), true)),
      node('details', { class: 'kg-ritual__meaning' }, node('summary', {}, this.ui.sequence), sequence), this.keepActions());
  }

  practiseScreen() {
    const plan = makePlan(this.profile, this.content);
    const step = plan.steps[this.index];
    const variant = this.variants[step.id];
    return node('div', {}, node('p', { class: 'kg-ritual__eyebrow' }, `${this.ui.step_label} ${this.index + 1} ${this.ui.of} 5`),
      node('progress', { max: '5', value: this.index + 1, 'aria-label': this.ui.step_label }),
      this.heading(step.title), node('p', { class: 'kg-ritual__instruction', 'data-kr-instruction': '' }, step[variant] || step.body),
      step.id === 'name' ? node('blockquote', { class: 'kg-ritual__phrase' }, plan.phrase) : null,
      step.id === 'return' ? node('div', { class: 'kg-ritual__after' }, node('h3', {}, this.ui.afterwards), node('p', {}, plan.action)) : null,
      node('div', { class: 'kg-ritual__adapt' }, this.button(this.ui.alternative, () => { this.variants[step.id] = variant === 'alternative' ? 'base' : 'alternative'; this.render(false); this.say(this.ui.alternative); }, true),
        this.button(this.ui.quiet, () => { this.variants[step.id] = 'quiet'; this.render(false); this.say(this.ui.quiet); }, true)),
      node('div', { class: 'kg-ritual__actions' }, this.button(this.ui.done, () => { this.skipped = this.skipped.filter(id => id !== step.id); this.advance(); }), this.button(this.ui.pause, () => this.navigate('paused'), true)),
      node('div', { class: 'kg-ritual__actions' }, this.button(this.ui.skip, () => { this.skipped = [...new Set([...this.skipped, step.id])]; this.advance(); }, true), this.index > 0 ? this.button(this.ui.step_back, () => { this.index--; this.render(); }, true) : null));
  }
  advance() {
    if (this.index === 4) this.navigate('complete');
    else { this.index++; this.render(); }
  }
  pausedScreen() {
    return node('div', {}, this.heading(this.ui.paused, this.ui.paused_body),
      node('div', { class: 'kg-ritual__actions' }, this.button(this.ui.resume, () => this.navigate('practise')), this.button(this.ui.edit, () => this.navigate('preview'), true)), this.keepActions());
  }
  completeScreen() {
    const plan = makePlan(this.profile, this.content);
    return node('div', {}, this.heading(this.ui.completed, this.ui.complete_body), node('blockquote', { class: 'kg-ritual__phrase' }, plan.phrase),
      node('div', { class: 'kg-ritual__after' }, node('h3', {}, this.ui.afterwards), node('p', {}, plan.action)),
      this.skipped.length ? node('p', { class: 'kg-ritual__hint' }, `${this.ui.skipped}: ${this.skipped.map(id => this.content.phases[id]).join(', ')}`) : null,
      this.keepActions(), this.button(this.ui.restart, () => { if (window.confirm(this.ui.restart_confirm)) { this.profile = normaliseProfile(); this.history = []; this.suggestion = null; this.variants = {}; this.skipped = []; this.index = 0; this.navigate('intention'); } }, true));
  }

  readSaved() {
    try { this.saved = restore(localStorage.getItem(STORAGE_KEY)); } catch { this.saved = null; }
  }
  save() {
    try {
      const data = snapshot(this.profile, this);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      this.saved = data;
      this.render(false);
      this.say(this.ui.saved);
    } catch { this.say(this.ui.storage_error); }
  }
  loadSaved() {
    this.readSaved();
    if (!this.saved) { this.say(this.ui.storage_error); return; }
    const data = this.saved;
    this.profile = data.profile;
    this.index = data.index;
    this.skipped = data.skipped;
    this.variants = data.variants;
    this.navigate(data.stage);
  }
  forget() {
    try { localStorage.removeItem(STORAGE_KEY); this.saved = null; this.render(false); this.say(this.ui.forgotten); }
    catch { this.say(this.ui.storage_error); }
  }
  download() {
    const text = exportText(makePlan(this.profile, this.content), this.content, this);
    const url = URL.createObjectURL(new Blob([text], { type: 'text/plain;charset=utf-8' }));
    const link = node('a', { href: url, download: `karmagick-${this.locale === 'it' ? 'rituale' : 'ritual'}.txt` });
    document.body.append(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
    this.say(this.ui.downloaded);
  }

  render(focus = true) {
    const active = this.panel.contains(document.activeElement) ? document.activeElement : null;
    const previous = active ? { id: active.id, name: active.name, value: active.value, tag: active.tagName, text: active.textContent } : null;
    this.fieldIndex = 0;
    const screens = { intention: 'intentionScreen', focus: 'focusScreen', context: 'contextScreen', words: 'wordsScreen', preview: 'previewScreen', practise: 'practiseScreen', paused: 'pausedScreen', complete: 'completeScreen' };
    this.panel.dataset.stage = this.stage;
    this.panel.replaceChildren(this[this.stage in screens ? screens[this.stage] : 'intentionScreen']());
    const part = { intention: 1, focus: 2, context: 3, words: 4, preview: 5 }[this.stage];
    this.querySelector('[data-ritual-progress]').textContent = part ? `${this.ui.part_label} ${part} ${this.ui.of} 5` : this.ui.guide_label;
    if (focus) {
      this.panel.querySelector('h2')?.focus({ preventScroll: true });
      this.panel.scrollIntoView({ block: 'start', behavior: 'instant' });
    } else if (previous) {
      // In-place updates must retain the keyboard user's position, including native checkboxes.
      const candidates = [...this.panel.querySelectorAll('button,input,textarea')];
      const target = candidates.find(item => previous.id ? item.id === previous.id : previous.name ? item.name === previous.name && item.value === previous.value : item.tagName === previous.tag && item.textContent === previous.text);
      target?.focus({ preventScroll: true });
    }
  }
}

if (!customElements.get('kg-ritual-guide')) customElements.define('kg-ritual-guide', RitualGuide);
